<template>

  <div>
    <b-row
      class="content-header"
    >

      <!-- Content Left -->
      <b-col
        class="content-header-left mb-2"
        cols="12"
        md="12"
      >
        <b-row class="breadcrumbs-top">
          <b-col cols="12">
            <h2 class="custom-header-title float-left pr-1 mb-0">
              Transactions
            </h2>
          </b-col>
        </b-row>
      </b-col>
    </b-row>

    <b-row class="mb-1">
      <b-col cols="12">
        <b-button
          v-for="tab in statusTabs"
          :key="tab.code"
          :variant="activeTab === tab.code ? 'primary' : 'outline-primary'"
          class="status-tab-button mr-1 mb-1"
          @click="activeTab = tab.code"
        >
          {{ tab.title }} <b-badge
            pill
            :variant="activeTab === tab.code ? 'light' : 'light-primary'"
            :class="{ 'active-tab-badge': activeTab === tab.code }"
            class="ml-50"
          >
            {{ tab.count }}
          </b-badge>
        </b-button>
      </b-col>
    </b-row>

    <!-- Table Container Card -->
    <b-card
      no-body
      class="mb-0"
    >
      <div class="m-2">
        <b-row>
          <b-col
            cols="12"
            md="3"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <b-form-checkbox
              class="mr-1"
              :checked="isAllSelected"
              @change="toggleSelectAll"
            />
            <b-dropdown
              v-if="canViewThisAction('update', 'Transaction')"
              variant="primary"
              text="Bulk actions"
            >
              <b-dropdown-item
                :disabled="selectedTrxIds.length === 0"
              >
                Export selected
              </b-dropdown-item>
            </b-dropdown>
            <span class="text-muted ml-1">Showing {{ dataMeta.from }} - {{ dataMeta.to }} of {{ dataMeta.of }}</span>
          </b-col>
          <b-col
            cols="12"
            md="9"
            class="d-flex flex-wrap align-items-center justify-content-md-end"
          >
            <b-input-group class="search-input mr-1 mb-1 mb-md-0 search_group">
              <b-input-group-prepend is-text>
                <feather-icon icon="SearchIcon" /> Search
              </b-input-group-prepend>
              <b-form-input
                v-model="searchQuery"
                placeholder="Search"
              />
              <b-input-group-append
                v-if="searchQuery"
                is-text
              >
                <feather-icon
                  icon="XIcon"
                  class="cursor-pointer"
                  @click="searchQuery = ''"
                />
              </b-input-group-append>
            </b-input-group>

            <b-form-input
              v-model="trxNoQuery"
              placeholder="Transaction No."
              class="filter-input mr-1 mb-1 mb-md-0"
            />

            <b-form-input
              v-model="relatedRecordIdQuery"
              placeholder="Related Record ID"
              class="filter-input mr-1 mb-1 mb-md-0"
            />

            <b-form-input
              v-model="paymentReferenceQuery"
              placeholder="Payment Reference"
              class="filter-input mr-1 mb-1 mb-md-0"
            />

            <b-button
              variant="filter_actions"
              @click="isFilterSidebarActive = true"
            >
              <feather-icon
                icon="FilterIcon"
                size="16"
                class="mr-50"
              />
              Filter
            </b-button>
          </b-col>
        </b-row>
      </div>

      <b-table
        class="position-relative min-height-table"
        :items="paginatedTransactions"
        responsive
        :fields="tableColumns"
        primary-key="trxNo"
        show-empty
        empty-text="No matching transactions found"
      >
        <template #cell(checkbox)="data">
          <b-form-checkbox
            v-model="selectedTrxIds"
            :value="data.item._id"
          />
        </template>

        <template #cell(trxNo)="data">
          <b-link class="weight-600 align-middle detail-view-id">
            {{ (data.item.stringID || '').replace(/^TX/, '') }}
          </b-link>
        </template>

        <template #cell(relatedRecord)="data">
          <b-link
            v-if="data.item.relatedRecordType === 'RetailOrder'"
            class="weight-600 align-middle detail-view-id"
            :to="{ name: 'customers-orders-show', params: { id: data.item.relatedRecordId } }"
          >
            {{ data.item.relatedRecordStringID }}
          </b-link>
          <span v-else>{{ data.item.relatedRecordStringID }}</span><br>
          <small class="text-muted">{{ data.item.relatedRecordType }}</small>
        </template>

        <template #cell(amount)="data">
          ${{ numberFormat(data.item.amount) }}
        </template>

        <template #cell(paymentReference)="data">
          {{ data.item.paymentReferenceLabel }}<br>
          <small class="text-muted">{{ data.item.paymentReferenceNo }}</small>
        </template>

        <template #cell(dateTime)="data">
          {{ dateFormatWithTime(data.item.createdAt) }}
        </template>

        <template #cell(processedBy)="data">
          <b-link class="weight-600 align-middle">
            {{ data.item.processedBy ? data.item.processedBy.name : '–' }}
          </b-link>
        </template>

        <template #cell(customer)="data">
          <b-link class="weight-600 align-middle">
            {{ data.item.customerName }}
          </b-link>
        </template>

        <template #cell(remarks)="data">
          {{ data.item.remarks || '–' }}
        </template>

        <template #cell(actions)="data">
          <b-dropdown
            variant="link"
            no-caret
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="canViewThisAction('update', 'Transaction')"
              v-b-modal.modal-edit-remarks
              @click="openEditRemarks(data.item)"
            >
              <span class="align-middle ml-50">Edit Remarks</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>
    </b-card>

    <div
      v-if="filteredTransactions.length > perPage"
      class="mx-2 mb-2"
    >
      <b-row>
        <!-- Pagination -->
        <b-col
          cols="12"
          sm="12"
          class="d-flex align-items-center justify-content-center pagination-content"
        >

          <b-pagination
            v-model="currentPage"
            :total-rows="filteredTransactions.length"
            :per-page="perPage"
            first-number
            last-number
            class="mb-0 mt-1 mt-sm-0"
            align="center"
            prev-text="Prev"
            next-text="Next"
          >
            <template #prev-text>
              <feather-icon
                icon="ChevronLeftIcon"
                size="18"
              />
              <span>Prev</span>
            </template>
            <template #next-text>
              <span>Next</span>
              <feather-icon
                icon="ChevronRightIcon"
                size="18"
              />
            </template>
          </b-pagination>

        </b-col>

      </b-row>
    </div>

    <transaction-filters
      :is-filter-sidebar-active.sync="isFilterSidebarActive"
      @apply-filter="onApplyFilter"
    />

    <edit-remarks-modal
      :remarks="editingRemarks"
      @save="onSaveRemarks"
    />
  </div>
</template>

<script>
import {
  BRow, BCol, BButton, BCard, BTable, BBadge, BDropdown, BDropdownItem, BFormCheckbox, BFormInput,
  BInputGroup, BInputGroupPrepend, BInputGroupAppend, BLink, VBModal, BPagination,
} from 'bootstrap-vue'
import { useUtils as useAclUtils } from '@/libs/acl/custom'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import TransactionFilters from './TransactionFilters.vue'
import EditRemarksModal from './EditRemarksModal.vue'

const { canViewThisAction } = useAclUtils()

export default {
  components: {
    BRow,
    BCol,
    BButton,
    BCard,
    BTable,
    BBadge,
    BDropdown,
    BDropdownItem,
    BFormCheckbox,
    BFormInput,
    BInputGroup,
    BInputGroupPrepend,
    BInputGroupAppend,
    BLink,
    BPagination,
    TransactionFilters,
    EditRemarksModal,
    // eslint-disable-next-line vue/no-unused-components
    ToastificationContent,
  },
  directives: {
    'b-modal': VBModal,
  },

  data() {
    return {
      canViewThisAction,
      activeTab: 'All',
      isFilterSidebarActive: false,
      editingTransaction: null,
      editingRemarks: '',
      searchQuery: '',
      trxNoQuery: '',
      relatedRecordIdQuery: '',
      paymentReferenceQuery: '',
      appliedFilters: null,
      tableColumns: [
        { key: 'checkbox', label: '' },
        { key: 'trxNo', label: 'Trx No.', sortable: true },
        { key: 'relatedRecord', label: 'Related Record' },
        { key: 'paymentMode', label: 'Payment Mode', sortable: true },
        { key: 'amount', label: 'Amount (S$)', sortable: true },
        { key: 'paymentReference', label: 'Payment Reference' },
        { key: 'dateTime', label: 'Date/Time', sortable: true },
        { key: 'processedBy', label: 'Processed By', sortable: true },
        { key: 'customer', label: 'Customer', sortable: true },
        { key: 'remarks', label: 'Remarks', sortable: true },
        { key: 'actions', label: 'Action' },
      ],
      transactions: [],
      selectedTrxIds: [],
      counts: {
        allCount: 0, processingCount: 0, confirmedCount: 0, completedCount: 0, cancelledCount: 0,
      },
      currentPage: 1,
      perPage: Number(process.env.VUE_APP_PER_PAGE_CONTENT) || 10,
    }
  },

  computed: {
    statusTabs() {
      return [
        { title: 'Processing', code: 'Processing', count: this.counts.processingCount },
        { title: 'Confirmed', code: 'Confirmed', count: this.counts.confirmedCount },
        { title: 'Completed', code: 'Completed', count: this.counts.completedCount },
        { title: 'Cancelled', code: 'Cancelled', count: this.counts.cancelledCount },
        { title: 'All Orders', code: 'All', count: this.counts.allCount },
      ]
    },
    filteredTransactions() {
      let list = this.transactions

      if (this.activeTab !== 'All') {
        list = list.filter(t => t.status === this.activeTab)
      }

      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        list = list.filter(t => (t.customerName || '').toLowerCase().includes(query))
      }

      if (this.trxNoQuery) {
        const query = this.trxNoQuery.toLowerCase()
        list = list.filter(t => (t.stringID || '').toLowerCase().includes(query))
      }

      if (this.relatedRecordIdQuery) {
        const query = this.relatedRecordIdQuery.toLowerCase()
        list = list.filter(t => (t.relatedRecordStringID || '').toLowerCase().includes(query))
      }

      if (this.paymentReferenceQuery) {
        const query = this.paymentReferenceQuery.toLowerCase()
        list = list.filter(t => (t.paymentReferenceNo || '').toLowerCase().includes(query))
      }

      if (this.appliedFilters) {
        const filters = this.appliedFilters

        if (filters.relatedRecords && filters.relatedRecords.length) {
          list = list.filter(t => filters.relatedRecords
            .some(r => this.normalizeLabel(r) === this.normalizeLabel(t.relatedRecordType)))
        }

        if (filters.transactionDate) {
          list = list.filter(t => t.createdAt && this.dateFormat(t.createdAt) === filters.transactionDate)
        }

        if (filters.paymentMode) {
          list = list.filter(t => t.paymentMode === filters.paymentMode)
        }

        if (filters.amountRange) {
          list = list.filter(t => this.isAmountInRange(t.amount, filters.amountRange))
        }

        if (filters.processedBy) {
          list = list.filter(t => t.processedBy && t.processedBy.name === filters.processedBy)
        }
      }

      return list
    },
    isAllSelected() {
      return this.filteredTransactions.length > 0 && this.filteredTransactions.every(t => this.selectedTrxIds.includes(t._id))
    },
    paginatedTransactions() {
      const start = (this.currentPage - 1) * this.perPage
      return this.filteredTransactions.slice(start, start + this.perPage)
    },
    dataMeta() {
      const total = this.filteredTransactions.length
      const localCount = this.paginatedTransactions.length
      return {
        from: this.perPage * (this.currentPage - 1) + (localCount ? 1 : 0),
        to: this.perPage * (this.currentPage - 1) + localCount,
        of: total,
      }
    },
  },

  watch: {
    activeTab() {
      this.currentPage = 1
    },
    searchQuery() {
      this.currentPage = 1
    },
    trxNoQuery() {
      this.currentPage = 1
    },
    relatedRecordIdQuery() {
      this.currentPage = 1
    },
    paymentReferenceQuery() {
      this.currentPage = 1
    },
    appliedFilters() {
      this.currentPage = 1
    },
  },

  mounted() {
    if (this.$route.query.trxNo) {
      this.trxNoQuery = this.$route.query.trxNo
    }
    this.fetchTransactions()
    this.fetchCounts()
  },

  methods: {
    normalizeLabel(value) {
      return (value || '').toString().toLowerCase().replace(/\s+/g, '')
    },
    isAmountInRange(amount, range) {
      const value = Number(amount) || 0
      switch (range) {
        case 'Below $500': return value < 500
        case '$500 - $1,000': return value >= 500 && value <= 1000
        case '$1,000 - $5,000': return value > 1000 && value <= 5000
        case 'Above $5,000': return value > 5000
        default: return true
      }
    },
    onApplyFilter(filters) {
      this.appliedFilters = filters
    },
    toggleSelectAll(checked) {
      const visibleIds = this.filteredTransactions.map(t => t._id)
      this.selectedTrxIds = checked
        ? [...new Set([...this.selectedTrxIds, ...visibleIds])]
        : this.selectedTrxIds.filter(id => !visibleIds.includes(id))
    },
    openEditRemarks(transaction) {
      this.editingTransaction = transaction
      this.editingRemarks = transaction.remarks
    },
    onSaveRemarks(remarks) {
      if (!this.editingTransaction) return

      const formData = new FormData()
      formData.append('remarks', remarks || '')

      this.$http.patch(`sales/transactions/${this.editingTransaction._id}/remarks/update`, formData)
        .then(response => {
          this.editingTransaction.remarks = response.data.data.remarks
        })
        .catch(error => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    },
    fetchTransactions() {
      this.$http.get('sales/transactions', { params: { perPage: 1000, sortBy: 'createdAt', sortDesc: true } })
        .then(response => {
          this.transactions = response.data.transactions
        })
        .catch(error => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    },
    fetchCounts() {
      this.$http.get('sales/transactions/counts')
        .then(response => {
          this.counts = response.data
        })
        .catch(() => {})
    },
  },
}
</script>

<style lang="scss" scoped>
.search-input {
  max-width: 220px;
}
.filter-input {
  max-width: 170px;
}

.status-tab-button {
  &.btn-outline-primary {
    border-color: #104D9D;
    color: #104D9D;
  }
  &.btn-primary {
    color: #FFFFFF;
  }
}

.active-tab-badge {
  background-color: #FFFFFF;
  color: #104D9D;
}
</style>
